import {
  Response,
  GeneralLedgerTransaction,
  Account,
  AccountBalance,
  AccountType,
  GeneralLedgerTransactionFormValues,
  GeneralLedgerStatus,
  apiAbortController,
  Document,
  AccountingJobProps as AccountingJob,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import api from '../../../api/api';
import { GeneralLedgerTransactionValidator } from '../types';
import { Balance } from '../../../types/account';

export const glTransactionsListApi = api.injectEndpoints({
  endpoints: (build) => ({
    getGeneralLedgerList: build.query<
      Response<GeneralLedgerTransaction>,
      string
    >({
      query: (query) => ({
        url: `/v1/general-ledger/transactions${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGeneralLedgerListStatuses: build.query<
      Response<{
        status: GeneralLedgerStatus;
        id: string;
      }>,
      string
    >({
      query: (query) => ({
        url: `/v1/general-ledger/transactions/statuses${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsAccountBalanceById: build.query<
      Response<AccountBalance>,
      {
        id: string;
        query?: string;
        typeAcc: AccountType.CREDIT | AccountType.DEBIT;
      }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true${data.query || ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGeneralTransactionById: build.query<
      { data: GeneralLedgerTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/general-ledger/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGLTransactionValidators: build.query<
      { data: GeneralLedgerTransactionValidator },
      void
    >({
      query: () => ({
        url: '/v1/general-ledger/transactions/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createGLTransaction: build.mutation<
      { data: GeneralLedgerTransaction },
      GeneralLedgerTransactionFormValues
    >({
      query: (body) => ({
        url: '/v1/general-ledger/transactions',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteGLTransaction: build.mutation<
      { data: GeneralLedgerTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/general-ledger/transactions/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsAccountsList: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionsAccountsCategories: build.query<Response<Category>, string>({
      query: (query) => ({
        url: `/v1/accounts-categories?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllDebitAccountBalances: build.query<Response<Balance>, { id: string }>({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllCreditAccountBalances: build.query<Response<Balance>, { id: string }>(
      {
        query: (data) => ({
          url: `/v1/accounts/${data.id}/balances?all=true`,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getGLDocuments: build.query<
      Response<Document>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents${
          data.cursor ? `?cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGLDocument: build.query<
      { data: Document },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportGLTransaction: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/general-ledger/transactions/export',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addDocumentToGLTransaction: build.mutation<
      { data: Document },
      {
        id: string;
        body: {
          name: string;
          uploadId: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteGLDocuments: build.mutation<
      {
        data: { count: number };
      },
      {
        id: string;
        body: {
          ids: string[];
        };
      }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents/batch-delete`,
        method: 'DELETE',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGLAccountingJobs: build.query<Response<AccountingJob>, string>({
      query: (query) => ({
        url: `/v1/accounting-jobs${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getGLAccountingJobsStates: build.query<Response<AccountingJob>, string>({
      query: (query) => ({
        url: `/v1/accounting-jobs${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetGeneralLedgerListQuery,
  useLazyGetGeneralTransactionByIdQuery,
  useLazyGetTransactionsAccountBalanceByIdQuery,
  useGetGLTransactionValidatorsQuery,
  useCreateGLTransactionMutation,
  useDeleteGLTransactionMutation,
  useLazyGetTransactionsAccountsListQuery,
  useGetTransactionsAccountsCategoriesQuery,
  useLazyGetGeneralLedgerListStatusesQuery,
  useLazyGetAllCreditAccountBalancesQuery,
  useLazyGetAllDebitAccountBalancesQuery,
  useExportGLTransactionMutation,
  useLazyGetGLDocumentsQuery,
  useAddDocumentToGLTransactionMutation,
  useLazyGetGLDocumentQuery,
  useDeleteGLDocumentsMutation,
  useLazyGetGLAccountingJobsQuery,
  useLazyGetGLAccountingJobsStatesQuery,
} = glTransactionsListApi;

export const {
  endpoints: {
    getGeneralLedgerList,
    getTransactionsAccountBalanceById,
    getGeneralTransactionById,
    getGLTransactionValidators,
    createGLTransaction,
    deleteGLTransaction,
    getTransactionsAccountsList,
    getGeneralLedgerListStatuses,
    getAllCreditAccountBalances,
    getAllDebitAccountBalances,
    exportGLTransaction,
    getGLDocuments,
    addDocumentToGLTransaction,
    getGLDocument,
    deleteGLDocuments,
    getGLAccountingJobs,
    getGLAccountingJobsStates,
  },
} = glTransactionsListApi;
